import { BCMSRecord, NavLink, useGlobalController } from "@bcm-dev/bcms-vite";
import { AddBiller, Bill, Biller } from "model";
import React from "react";

import UITextInput from "compid/ui/UITextInput";
import { ImageAsset, SVGAsset } from "res";

import "./styles.css";

const SearchForBillerContext = React.createContext(null);
/**
 * @function SearchForBillerField
 * @param {STRING} placeholder - The placeholder for the input field
 * @param {STRING} className - The class that should be applied to the field wrapper
 * @returns {React.Component}
 * @description Search field that displays available billers currently added, can be added or the ability to create a new biller
 * @exported true
 * @example
 * <SearchForBillerField />
 */
export default function SearchForBillerField(props) {
    const globalController = useGlobalController();
    BCMSRecord.globalControllerFor([ Biller, AddBiller, Bill ], globalController);

    const placeholder = props.placeholder ?? "";
    const className = props.className ?? "";
    const dataPoint = props.dataPoint ? { "data-point" : props.dataPoint } : {};
    const state = props.searchState;
    
    const controller = {
        "toggle" : value => props.setSearchState({ ...state, "dropdown" : value }),
        "set" : value => {
            props.setSearchState({ ...value })
    },

        "state" : props.searchState
    };

    const update = value => controller.set({ "value" : value, "dropdown" : !(value.length === 0) });
    const close = evt => controller.toggle(false);

    document.addEventListener("force-biller-search-state", evt => {
        const { value, showDropdown } = evt.detail.value;
        controller.set({ value, dropdown: showDropdown ?? false });
    });

    return (
        <SearchForBillerContext.Provider value={ controller }>
            <div className={`search-for-biller-field form-row flex-height ${ className }`} { ...dataPoint }>
                <div className="search-field-content floating-label-input-container UITextInput">
                    <UITextInput className="floating-label-input form-row-input" onChange={ update } placeHolder={ placeholder } value={state.value} dataPoint={ props.dataPoint } />
                    <label className="floating-label form-row-label">{ props.placeHolder }</label>
                    <SVGAsset className="search-icon" id="MagnifyingGlassIcon" data-point={`Search_For_Biller_Search_Icon`} />
                   
                    { state.dropdown && <SearchForBillerDropdown /> }
                </div>
                
                { state.dropdown && <div className="dropdown-overlay" onClick={ close }></div> }
            </div>
        </SearchForBillerContext.Provider>
    );
}

export const useSearchForBillerContext = () => React.useContext(SearchForBillerContext);
/**
 * @function SearchForBillerDropdown
 * @returns {React.Component}
 * @description Dropdown component for billers shown in the SearchForBillerField
 * @note This component needs to be within the context of the SearchForBillerField in order to access the useSearchForBillerContext context
 * @exported true
 * @example
 * <SearchForBillerDropdown />
 */
export function SearchForBillerDropdown(props) {
    const { state } = useSearchForBillerContext();
    const filterName = billers => billers.filter(biller => biller.name.toLowerCase().includes(state.value.toLowerCase()), []);

    const billers = filterName(Biller.initAll().filter(item => item.addType !== "quick_add" && item.addType !== "verification_add", []));
    const addBillers = filterName(AddBiller.initAll());

    const dropdownItems = billers.map(item => <SearchForBillerDropdownItem key={ `search_biller_${ item.id }` } biller={ item } />, [])
    
    const showNoBillers = dropdownItems.length === 0;

    return (
        <div className="search-for-biller-dropdown">
            { showNoBillers && <SearchForBillerNoBillers /> }
            { dropdownItems }
        </div>
    );
}
/**
 * @function SearchForBillerDropdownItem
 * @param {Biller} biller - The biller instance to be used for the item
 * @returns {React.Component}
 * @exported true
 * @description Primary dropdown item for the SearchForBillerDropdown
 * @example
 * <SearchForBillerDropdownItem biller={ biller } />
 */
export function SearchForBillerDropdownItem(props) {
    const biller = props.biller;
    const bill = Bill.getBillByBiller(biller.id) ??{};

    return (
        <div>
            <NavLink to={ `/makeAPayment/${ bill.id }` }>
                <div className="row-main-content flex-container flex-horizontal align-center dropdown-item">
                    <div className="logo-container set-width">
                        <ImageAsset id={ biller.logo } className="logo-icon" />
                    </div>

                    <div className="flex-width flex-container flex-vertical">
                        <p className="flex-item"><b>{ biller.name }</b></p>
                        <p className="flex-item">{ biller.number }</p>
                    </div>
                </div>
            </NavLink>
        </div>
    );
}
/**
 * @function SearchForBillerAddKnown
 * @param {Biller} biller - The biller instance to be used for the item
 * @returns {React.Component}
 * @exported true
 * @description Dropdown item for billers that can be added but haven't been already
 * @example 
 * <SearchForBillerAddKnown biller={ biller } />
 */
export function SearchForBillerAddKnown(props) {
    const biller = props.biller;

    return (
        <div>
            <NavLink to={ `/addBillerWorkflow/${ biller.id }/1` }>
                <div className="row-main-content flex-container flex-horizontal align-center dropdown-item">
                    <div className="logo-container set-width">
                        <SVGAsset id="PlusSign" />
                    </div>

                    <div className="flex-width flex-container flex-vertical">
                        <p className="flex-item">
                            <b >{ biller.name }</b>
                        </p>
                        <p className="flex-item">Add biller</p>
                    </div>
                </div>
            </NavLink>
        </div>
    );
}
/**
 * @function SearchForBillerNoBillers
 * @returns {React.Component}
 * @exported true
 * @note This component needs to be within the context of the SearchForBillerField in order to access the useSearchForBillerContext context
 * @description Dropdown item to show that no results were found but can create a new biller
 * @example
 * <SearchForBillerNoBillers />
 */
export function SearchForBillerNoBillers(props) {

    
    return (
        <>
            <div className="row-main-content flex-container flex-horizontal align-center no-match-found dropdown-item">
                <p>No matches found. Select <b>Add biller</b> to continue.</p>
            </div>

        </>
    );
}